import React, { useEffect, useRef, useState } from "react";
import {FlatList, Image, StyleSheet, Text, View} from "react-native";
import {ArrowLeft, CheckCircle2, ChevronDown, ChevronRight, Play, RotateCcw, X} from "lucide-react-native";
import {JellyfinItem, imageSource} from "../../api/jellyfin";
import {formatPlayerTime} from "../../player/presentation";
import {Credentials} from "../../storage/credentials";
import {focusRegisteredNode, getFocusModality} from "../../tvNavigation/registry";
import {FocusablePressable} from "../FocusablePressable";
import {playerFont} from "../../theme/tokens";

export function PlayerEpisodesMenu({ credentials, currentItemId, seasons, episodes, selectedSeason, selectedSeasonId, television, shortLandscape, seasonError, episodeError, episodeLoading, seasonFetching, episodeFetching, onSelectSeason, onPlayEpisode, onClose, onRetrySeasons, onRetryEpisodes }: { credentials: Credentials; currentItemId?: string; seasons: JellyfinItem[]; episodes: JellyfinItem[]; selectedSeason?: JellyfinItem; selectedSeasonId: string; television: boolean; shortLandscape: boolean; seasonError: boolean; episodeError: boolean; episodeLoading: boolean; seasonFetching: boolean; episodeFetching: boolean; onSelectSeason: (id: string) => void; onPlayEpisode: (episode: JellyfinItem) => void; onClose: () => void; onRetrySeasons: () => void; onRetryEpisodes: () => void }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const episodeListRef = useRef<FlatList<JellyfinItem>>(null);
  useEffect(() => { setPickerOpen(false); }, [currentItemId]);
  const stepSeason = (delta: -1 | 1) => {
    const currentIndex = seasons.findIndex((entry) => entry.Id === selectedSeasonId);
    const next = seasons[Math.max(0, Math.min(seasons.length - 1, (currentIndex < 0 ? 0 : currentIndex) + delta))];
    if (next?.Id && next.Id !== selectedSeasonId) onSelectSeason(next.Id);
  };
  const artworkWidth = shortLandscape ? 118 : television ? 168 : 148;
  const artworkHeight = Math.round(artworkWidth * 9 / 16);
  const cleanEpisodeTitle = (name?: string) => (name ?? "Episode").replace(/^S\d+\s*E\d+\s*[-:·.]?\s*/i, "") || "Episode";
  const currentEpisodeIndex = episodes.findIndex((episode) => episode.Id === currentItemId);
  const currentEpisode = currentEpisodeIndex >= 0 ? episodes[currentEpisodeIndex] : undefined;
  const currentEpisodeFocusId = currentEpisode ? `player.episode.${currentEpisode.Id ?? currentEpisodeIndex}` : undefined;
  useEffect(() => {
    if (currentEpisodeIndex < 0) return undefined;
    const timer = setTimeout(() => {
      episodeListRef.current?.scrollToIndex({ index: currentEpisodeIndex, animated: false, viewPosition: 0 });
    }, 0);
    return () => clearTimeout(timer);
  }, [currentEpisodeIndex, selectedSeasonId]);
  useEffect(() => {
    if (!currentEpisodeFocusId || getFocusModality() === "pointer") return undefined;
    let attempts = 0;
    let frame: number | undefined;
    const focusCurrentEpisode = () => {
      if (focusRegisteredNode(currentEpisodeFocusId) || attempts >= 6) return;
      attempts += 1;
      frame = requestAnimationFrame(focusCurrentEpisode);
    };
    frame = requestAnimationFrame(focusCurrentEpisode);
    return () => { if (frame !== undefined) cancelAnimationFrame(frame); };
  }, [currentEpisodeFocusId]);
  return (
    <View style={styles.episodeBrowserBody}>
      <View style={styles.episodeBrowserHeaderBlock}>
        <View style={styles.episodeBrowserHeader}>
          <FocusablePressable accessibilityLabel="Close episodes" onPress={() => { setPickerOpen(false); onClose(); }} style={(focused, pressed, hovered) => [styles.episodeBrowserBack, (focused || hovered) && styles.episodeBrowserBackActive, pressed && styles.pressed]}><X color="#fff" size={18} /></FocusablePressable>
          {television ? (seasons.length > 1 ? <View style={styles.episodeSeasonStepper}><FocusablePressable accessibilityLabel="Previous season" disabled={seasons.findIndex((entry) => entry.Id === selectedSeasonId) <= 0} onPress={() => stepSeason(-1)} style={(focused, pressed, hovered) => [styles.episodeSeasonStep, (focused || hovered) && styles.episodeBrowserBackActive, pressed && styles.pressed]}><ArrowLeft color="#fff" size={14} /></FocusablePressable><Text numberOfLines={1} style={styles.episodeBrowserSeason}>{selectedSeason?.Name ?? `Season ${selectedSeason?.IndexNumber ?? 1}`}</Text><FocusablePressable accessibilityLabel="Next season" disabled={seasons.findIndex((entry) => entry.Id === selectedSeasonId) >= seasons.length - 1} onPress={() => stepSeason(1)} style={(focused, pressed, hovered) => [styles.episodeSeasonStep, (focused || hovered) && styles.episodeBrowserBackActive, pressed && styles.pressed]}><ChevronRight color="#fff" size={14} /></FocusablePressable></View> : <Text numberOfLines={1} style={styles.episodeBrowserSeason}>{selectedSeason?.Name ?? `Season ${selectedSeason?.IndexNumber ?? 1}`}</Text>) : (seasons.length > 1 ? <FocusablePressable accessibilityLabel={`Choose season, ${selectedSeason?.Name ?? `Season ${selectedSeason?.IndexNumber ?? 1}`}`} accessibilityState={{ expanded: pickerOpen }} onPress={() => setPickerOpen((value) => !value)} style={(focused, pressed, hovered) => [styles.episodeSeasonTrigger, (focused || hovered) && styles.episodeSeasonTriggerActive, pressed && styles.pressed]}><Text numberOfLines={1} style={styles.episodeBrowserSeason}>{selectedSeason?.Name ?? `Season ${selectedSeason?.IndexNumber ?? 1}`}</Text><ChevronDown color="rgba(255,255,255,0.84)" size={14} style={{ transform: [{ rotate: pickerOpen ? "180deg" : "0deg" }] }} /></FocusablePressable> : <Text numberOfLines={1} style={styles.episodeBrowserSeason}>{selectedSeason?.Name ?? `Season ${selectedSeason?.IndexNumber ?? 1}`}</Text>)}
          <View style={styles.episodeBrowserHeaderSpacer} /><Text style={styles.episodeBrowserLabel}>{episodes.length} EPISODES</Text>
        </View>
        {pickerOpen && !television ? <FlatList data={seasons} keyExtractor={(season, index) => season.Id ?? `season-${index}`} style={styles.episodeSeasonMenu} renderItem={({ item: season, index }) => { const selected = season.Id === selectedSeasonId; const label = season.Name ?? `Season ${season.IndexNumber ?? index + 1}`; return <FocusablePressable accessibilityLabel={`Show ${label}`} accessibilityState={{ selected }} onPress={() => { if (season.Id && !selected) onSelectSeason(season.Id); setPickerOpen(false); }} style={(focused, pressed, hovered) => [styles.episodeSeasonMenuRow, selected && styles.episodeSeasonMenuRowSelected, (focused || hovered) && styles.episodeSeasonMenuRowActive, pressed && styles.pressed]}><Text style={[styles.episodeSeasonButtonText, selected && styles.episodeSeasonButtonTextSelected]}>{label}</Text>{selected ? <Text style={styles.menuCheck}>SELECTED</Text> : null}</FocusablePressable>; }} /> : null}
      </View>
      <FlatList
        ref={episodeListRef}
        data={episodes}
        showsVerticalScrollIndicator={false}
        keyExtractor={(episode, index) => episode.Id ?? `episode-${index}`}
        style={styles.episodeList}
        contentContainerStyle={styles.episodeBrowserList}
        onScrollToIndexFailed={(info) => {
          episodeListRef.current?.scrollToOffset({ offset: Math.max(0, info.averageItemLength * info.index), animated: false });
          setTimeout(() => episodeListRef.current?.scrollToIndex({ index: info.index, animated: false, viewPosition: 0 }), 40);
        }}
        renderItem={({ item: episode, index }) => {
          const current = episode.Id === currentItemId;
          const progress = Math.max(0, Math.min(1, episode.UserData?.PlayedPercentage !== undefined ? episode.UserData.PlayedPercentage / 100 : (episode.RunTimeTicks ?? 0) > 0 ? (episode.UserData?.PlaybackPositionTicks ?? 0) / (episode.RunTimeTicks ?? 1) : 0));
          const episodeImage = episode.Id ? imageSource(credentials, episode, "Primary", 320) : undefined;
          return <FocusablePressable accessibilityLabel={`${current ? "Current episode, " : ""}Episode ${episode.IndexNumber ?? index + 1}, ${cleanEpisodeTitle(episode.Name)}`} accessibilityState={{ selected: current }} focusId={`player.episode.${episode.Id ?? index}`} focusGroup="player.episodes" focusRole="card" onPress={() => { if (current) onClose(); else onPlayEpisode(episode); }} style={(focused, pressed, hovered) => [styles.episodeBrowserCard, shortLandscape && styles.episodeBrowserCardShort, current && styles.episodeBrowserCardCurrent, (focused || hovered) && styles.episodeBrowserCardActive, pressed && styles.pressed]}>
            {(focused, hovered) => <>
              <View style={[styles.episodeArtwork, { width: artworkWidth, height: artworkHeight }]}>{episodeImage ? <Image source={episodeImage} resizeMode="cover" style={StyleSheet.absoluteFillObject} /> : null}{progress > 0 && progress < 1 ? <View style={styles.episodeProgressTrack}><View style={[styles.episodeProgressFill, { width: `${Math.round(progress * 100)}%` }]} /></View> : null}{!current && (focused || hovered) ? <View style={styles.episodePlayBadge}><Play color="#111" fill="#111" size={18} /></View> : null}</View>
              <View style={styles.episodeCopy}><View style={styles.episodeTitleRow}><Text style={styles.episodeNumber}>E{episode.IndexNumber ?? index + 1}</Text><Text numberOfLines={1} style={[styles.browserEpisodeTitle, television && styles.browserEpisodeTitleTelevision]}>{cleanEpisodeTitle(episode.Name)}</Text></View>{!shortLandscape && episode.Overview ? <Text numberOfLines={television ? 3 : 2} style={[styles.episodeOverview, television && styles.episodeOverviewTelevision]}>{episode.Overview}</Text> : null}<View style={styles.episodeMetaRow}><Text style={styles.episodeRuntime}>{episode.RunTimeTicks ? formatPlayerTime(episode.RunTimeTicks / 10_000_000) : "Runtime unavailable"}</Text>{episode.UserData?.Played ? <View style={styles.episodeStatus}><CheckCircle2 color="#8ff0b0" size={13} /><Text style={styles.episodeWatched}>Watched</Text></View> : null}</View></View>
            </>}
          </FocusablePressable>;
        }}
        ListEmptyComponent={<View style={styles.episodeEmptyState}><Text style={styles.episodeEmpty}>{seasonError ? "Could not load seasons." : episodeError ? `Could not load ${selectedSeason?.Name ?? "season"}.` : episodeLoading || episodeFetching ? `Loading ${selectedSeason?.Name ?? "season"}...` : "No episodes returned."}</Text>{seasonError || episodeError ? <FocusablePressable accessibilityLabel="Retry episode browser" onPress={() => { if (seasonError) onRetrySeasons(); else onRetryEpisodes(); }} style={(focused, pressed, hovered) => [styles.episodeRetry, (focused || hovered) && styles.episodeRetryActive, pressed && styles.pressed]}><RotateCcw color="#fff" size={14} /><Text style={styles.episodeRetryText}>{seasonFetching || episodeFetching ? "Retrying..." : "Retry"}</Text></FocusablePressable> : null}</View>}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  episodeBrowserBody: { minHeight: 0, flex: 1 },
  episodeBrowserHeaderBlock: {
    marginHorizontal: -8,
    marginTop: -8,
    marginBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: "rgba(255,255,255,0.12)",
    backgroundColor: "rgba(255,255,255,0.06)",
  },
  episodeBrowserHeader: {
    minHeight: 52,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  episodeBrowserBack: {
    width: 32,
    height: 32,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  episodeBrowserBackActive: { backgroundColor: "rgba(255,255,255,0.12)" },
  episodeBrowserSeason: {
    minWidth: 0,
    flexShrink: 1,
    color: "rgba(255,255,255,0.92)",
    fontFamily: playerFont,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "900",
  },
  episodeBrowserLabel: {
    color: "rgba(255,255,255,0.45)",
    fontFamily: playerFont,
    fontSize: 10,
    lineHeight: 13,
    fontWeight: "900",
  },
  episodeBrowserHeaderSpacer: { flex: 1 },
  episodeSeasonTrigger: {
    minWidth: 118,
    maxWidth: 260,
    height: 32,
    paddingHorizontal: 9,
    borderWidth: 1,
    borderColor: "rgba(229,9,20,0.34)",
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    backgroundColor: "rgba(229,9,20,0.14)",
  },
  episodeSeasonTriggerActive: {
    borderColor: "rgba(255,255,255,0.36)",
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  episodeSeasonStepper: {
    minWidth: 180,
    maxWidth: 280,
    height: 34,
    paddingHorizontal: 3,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.18)",
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 5,
    backgroundColor: "rgba(0,0,0,0.60)",
  },
  episodeSeasonStep: { width: 28, height: 28, borderRadius: 3, alignItems: "center", justifyContent: "center" },
  episodeSeasonMenu: { maxHeight: 150, marginHorizontal: 52, marginBottom: 8, borderWidth: 1, borderColor: "rgba(255,255,255,0.12)", borderRadius: 4, backgroundColor: "rgba(0,0,0,0.96)" },
  episodeSeasonMenuRow: { minHeight: 34, paddingHorizontal: 10, flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 8 },
  episodeSeasonMenuRowSelected: { backgroundColor: "rgba(229,9,20,0.18)" },
  episodeSeasonMenuRowActive: { backgroundColor: "rgba(255,255,255,0.10)" },
  episodeSeasonButtonText: {
    color: "rgba(255,255,255,0.62)",
    fontFamily: playerFont,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "800",
  },
  episodeSeasonButtonTextSelected: { color: "#fff" },
  episodeList: { minHeight: 0, flex: 1 },
  episodeBrowserList: { paddingHorizontal: 2, paddingBottom: 4, gap: 6 },
  episodeBrowserCard: {
    minHeight: 112,
    padding: 9,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.07)",
    borderRadius: 12,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    backgroundColor: "rgba(255,255,255,0.035)",
  },
  episodeBrowserCardShort: { minHeight: 84, padding: 7, gap: 9 },
  episodeBrowserCardCurrent: { borderColor: "rgba(229,9,20,0.32)", backgroundColor: "rgba(229,9,20,0.09)" },
  episodeBrowserCardActive: { borderColor: "rgba(255,255,255,0.24)", backgroundColor: "rgba(255,255,255,0.10)", transform: [{ translateY: -2 }] },
  episodeArtwork: {
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "rgba(255,255,255,0.08)",
  },
  episodeProgressTrack: { position: "absolute", left: 0, right: 0, bottom: 0, height: 4, backgroundColor: "rgba(255,255,255,0.22)" },
  episodeProgressFill: { height: 4, backgroundColor: "#e50914" },
  episodePlayBadge: {
    position: "absolute",
    left: "50%",
    top: "50%",
    marginLeft: -19,
    marginTop: -19,
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.52)",
    backgroundColor: "rgba(255,255,255,0.90)",
    shadowColor: "#000",
    shadowOpacity: 0.5,
    shadowRadius: 7,
    shadowOffset: { width: 0, height: 3 },
    elevation: 7,
  },
  episodeNumber: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6, color: "rgba(255,255,255,0.88)", backgroundColor: "rgba(255,255,255,0.09)", fontFamily: playerFont, fontSize: 10, lineHeight: 14, fontWeight: "900" },
  episodeCopy: { minWidth: 0, flex: 1, paddingHorizontal: 2, paddingTop: 2 },
  episodeTitleRow: { minWidth: 0, flexDirection: "row", alignItems: "center", gap: 7 },
  browserEpisodeTitle: { minWidth: 0, flexShrink: 1, color: "#fff", fontFamily: playerFont, fontSize: 14, lineHeight: 19, fontWeight: "900" },
  browserEpisodeTitleTelevision: { fontSize: 16, lineHeight: 21 },
  episodeOverview: { marginTop: 6, color: "rgba(255,255,255,0.50)", fontFamily: playerFont, fontSize: 11, lineHeight: 15, fontWeight: "600" },
  episodeOverviewTelevision: { fontSize: 12, lineHeight: 17 },
  episodeMetaRow: { marginTop: 5, flexDirection: "row", alignItems: "center", gap: 10 },
  episodeRuntime: { marginTop: 4, color: "rgba(255,255,255,0.42)", fontFamily: playerFont, fontSize: 11, lineHeight: 14, fontWeight: "900" },
  episodeStatus: { flexDirection: "row", alignItems: "center", gap: 4 },
  episodeWatched: { color: "#8ff0b0", fontFamily: playerFont, fontSize: 10, lineHeight: 14, fontWeight: "900" },
  episodeEmptyState: { margin: 4, paddingHorizontal: 12, paddingVertical: 16, borderWidth: 1, borderColor: "rgba(255,255,255,0.12)", borderRadius: 6, alignItems: "flex-start", backgroundColor: "rgba(255,255,255,0.05)" },
  episodeEmpty: { color: "rgba(255,255,255,0.58)", fontFamily: playerFont, fontSize: 13, lineHeight: 18, fontWeight: "700" },
  episodeRetry: { minHeight: 32, marginTop: 10, paddingHorizontal: 10, borderWidth: 1, borderColor: "rgba(255,255,255,0.16)", borderRadius: 4, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 6, backgroundColor: "rgba(255,255,255,0.08)" },
  episodeRetryActive: { borderColor: "rgba(255,255,255,0.48)", backgroundColor: "rgba(255,255,255,0.14)" },
  episodeRetryText: { color: "#fff", fontFamily: playerFont, fontSize: 12, lineHeight: 16, fontWeight: "900" },
  menuCheck: { color: "#fca5a5", fontFamily: playerFont, fontSize: 9, lineHeight: 12, fontWeight: "900" },
  pressed: { opacity: 0.72 },
});
